import { Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';

import { Popover } from 'primeng/popover';
import { DomHandler } from 'primeng/dom';
import { Subscription, forkJoin, Observable } from 'rxjs';
import { map, filter, tap, switchMap } from 'rxjs/operators';

import { SourceGroupService } from '../../services/source-group.service';
import { SourceService } from '../../services/source.service';
import { SourceGroup } from '../../models/source-group.model';
import { SourceChip } from '../../models/source-chip.model';
import { ActionKeys } from '../../../Forms/enums/keys.enum';
import { ButtonTypesEnum } from '../../../Forms/enums/button-types.enum';
import { CoreDialogService } from '../../../../Core/Dialogs/services/dialog.service';
import { CreateEditSourceGroupDialogComponent } from '../CreateSourceGroupDialog/create-edit-source-group-dialog.component';

@Component({
    selector: 'agpr-source-group-chip',
    styleUrls: ['./source-group-chip.component.scss'],
    templateUrl: './source-group-chip.component.html',
    standalone: false
})
export class SourceGroupChipComponent implements OnInit, OnDestroy {

    @Input() id: string;
    @Input() groupId: number;

    @Output() remove: EventEmitter<number> = new EventEmitter<number>();

    @ViewChild('chipPopover') chipPopover: Popover;
    @ViewChild('chipContainer') chipContainer: ElementRef;

    group: SourceGroup;
    sourceChips: SourceChip[];

    private subscriptions: Subscription[] = [];

    readonly buttonTypes = ButtonTypesEnum;

    constructor(
        private sourceGroupService: SourceGroupService,
        private sourceService: SourceService,
        private coreDialogService: CoreDialogService
    ) { }

    ngOnInit() {
        this.subscriptions.push(
            this.sourceGroupService.refreshAllManual().pipe(
                switchMap(() => this.sourceGroupService.getById(Number(this.groupId)))
            ).subscribe(group => {
                this.group = group;
            })
        );
    }

    private panelKeydownListener = (event) => {
        this.onPanelKeydown(event);
    };

    private populateSourceChips(): Observable<SourceChip[]> {
        const requests = this.group.sourceIds.map(sourceId => {
            return this.sourceService.getById(sourceId).pipe(
                filter(x => !!x),
                map(source => source.toChipModel())
            );
        });

        return this.sourceService.refreshAllManual().pipe(
            switchMap(() => {
                return forkJoin(requests).pipe(
                    tap(chips => this.sourceChips = chips)
                );
            })
        );
    }

    showPanel(event, chipContainer) {
        this.populateSourceChips().subscribe(() => {
            this.chipPopover.baseZIndex = ++DomHandler.zindex;
            this.chipPopover.toggle(event, chipContainer);

            requestAnimationFrame(() => {
                const chipPopoverHTML = document.querySelector('.agpr-sourceGroupChip-popover') as HTMLElement;
                if (!chipPopoverHTML) {
                    return;
                }
                chipPopoverHTML.setAttribute('tabindex', '0');
                chipPopoverHTML.addEventListener('keydown', this.panelKeydownListener);
                chipPopoverHTML.focus();
            });
        });
    }

    /**
     * Handles closing the popover on escape or when the user attempts to tab out of the panel.
     * In this case focusout wont work because we have to catch the keystroke and prevent
     * it from moving the focus out of the DOM and into the browser controls.
     * @param event
     */
    onPanelKeydown(event: KeyboardEvent): void {
        switch(event.key) {
            case ActionKeys.ESCAPE:
                this.chipPopover.hide();
                this.onHide();
                break;
            case ActionKeys.TAB:
                if (((event.target as HTMLElement).parentElement.classList.contains('agpr-chipContainer-close') && event.shiftKey)
                    || ((event.target as HTMLElement).parentElement.classList.contains('agpr-chipContainer-chipsToken-icon') && !event.shiftKey)
                    || ((event.target as HTMLElement).classList.contains('p-overlaypanel') && !event.shiftKey)) {
                    // We only want to catch tab on the close icon OR shift + tab on the container or any chip button
                    return;
                }
                event.preventDefault();
                this.chipPopover.hide();
                this.onHide();
                break;
        }
    }

    /**
     * Handles resetting the focus when the popover closes.
     * Focuses the toggle button if present, otherwise the first chip
     */
    onHide(): void {
        requestAnimationFrame(() => {
            this.chipContainer.nativeElement.querySelector('.agpr-button')?.focus();
        });
    }

    editGroup() {
        this.chipPopover.hide();
        const io = {
            inputs: {
                group: this.group
            },
            outputs: {
                dialogClose: (group) => {
                    if (group) {
                        this.group = group;
                        this.populateSourceChips().subscribe();
                    }
                }
            }
        };
        this.coreDialogService.open(CreateEditSourceGroupDialogComponent, io);
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }
}
